/*
 * Copyright (c) 2019. World Wide Generation Ltd
 */

import React, { useCallback, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { Button } from 'reactstrap';
import { createCompanyOnboardingImport } from '../../actions/portfolio-tracker';
import { ROUTES } from '../../constants/routes';
import { generateUrl } from '../util';
import Papa from 'papaparse';
import { InitiativeSuggestion } from '../../constants/initiative';
import Fuse from 'fuse.js';
import { CompanyImportRow } from './CompanyImportCell';
import './styles.scss';
import { FileDropzoneVariant, ImportFileDropzone, MINE_TYPES } from '../../components/files/ImportFileDropZone';
import SimpleTooltip from '../../components/simple-tooltip';
import { CSVFormat, ConflictCompanies } from './ConflictCompanies';
import { updateRowData } from '../../components/survey/form/input/table/tableUtils';

interface CompanyImportProps {
  portfolioId: string;
  handleReload: () => void;
  existingCompanies?: InitiativeSuggestion[];
}

const addData = (data: CSVFormat[], fuse?: Fuse<InitiativeSuggestion>): CompanyImportRow[] => {
  return data.map((r, i) => {
    const matches = r.companyName && fuse ? fuse.search(r.companyName.replaceAll(' ', '|')) : [];
    const firstMatch = matches[0];
    const initiativeId = firstMatch?.score && firstMatch.score < 0.01 ? firstMatch.item._id : undefined;

    return {
      data: { ...r, initiativeId },
      id: i,
      // Get 3 results only
      matches: matches.slice(0, 3),
    };
  });
}

export const CompanyImport = ({ portfolioId, handleReload, existingCompanies }: CompanyImportProps) => {
  const history = useHistory();

  const [errorMessage, setMessage] = useState<string | undefined>();
  const [isSaving, setSaving] = useState(false);
  const [importFile, setImportFile] = useState<File | undefined>();
  const [dataArray, setData] = useState<CompanyImportRow[]>([]);
  const [fuseSearch, setFuse] = useState<Fuse<InitiativeSuggestion> | undefined>();

  const updateRow = useCallback(
    (row: Partial<CompanyImportRow>) => {
      setData((d) => updateRowData<CompanyImportRow>(d, row));
    },
    [setData]
  );

  React.useEffect(() => {
    if (existingCompanies) {
      setFuse(
        new Fuse(existingCompanies, {
          keys: ['name'],
          includeScore: true,
          useExtendedSearch: true,
          threshold: 0.5,
        })
      );
    }
  }, [existingCompanies]);

  React.useEffect(() => {
    if (!importFile) {
      setData([]);
      return;
    }

    Papa.parse<any, File>(importFile, {
      header: true,
      skipEmptyLines: true,
      complete: (results: Papa.ParseResult<CSVFormat>) => {
        setData(addData(results.data, fuseSearch));
      },
    });
  }, [importFile, fuseSearch]);

  const onFilesAdded = (files: File[]) => {
    const inputFile: File | undefined = files[0];
    if (!inputFile) {
      return;
    }
    setImportFile(inputFile);
  };

  const handleSubmit = () => {
    setMessage('');
    setSaving(true);

    const submitData = {
      portfolioId,
      companies: dataArray.map((r) => r.data),
    };

    // TODO: This api is being updated
    createCompanyOnboardingImport(portfolioId, submitData)
      .then(() => {
        handleReload();
        setSaving(false);
        history.push(generateUrl(ROUTES.PORTFOLIO_TRACKER_MANAGE_COMPANIES, { portfolioId }));
      })
      .catch((e) => {
        setMessage(e.message);
        setSaving(false);
      });
  };

  const hasImportData = dataArray.length > 0;
  const hasValidData = hasImportData && dataArray.every((d) => !!d.data.initiativeId);

  return (
    <>
      {errorMessage && <div className='alert alert-danger mt-4'>{errorMessage}</div>}
      <div className='company-import-container mt-4'>
        <p className='m-0'>
          All imported companies will be automatically created, with settings outlined in the{' '}
          <Link to='/files/csv-company-import.csv' target='_blank' download>
            import template
          </Link>{' '}
          . The designated owner will receive an invitation by email with instructions on how to activate their account.
          Required fields are:
        </p>
        <ul className='pl-3 mb-5 mt-2'>
          <li>Owner email</li>
          <li>Plan</li>
          <li>Company name</li>
          <li>HQ Country</li>
          <li>Reporting currency</li>
        </ul>

        <ImportFileDropzone
          variant={FileDropzoneVariant.Icon}
          accept={MINE_TYPES.CSV}
          importFile={importFile}
          onFilesAdded={onFilesAdded}
          onFileRemove={() => setImportFile(undefined)}
          hint='csv files only (max. 50 megabytes)'
          classes={{ wrapper: 'py-3' }}
        />

        <ConflictCompanies rows={dataArray} updateRow={updateRow} />

        <div className='mt-4 text-right'>
          <SimpleTooltip text={importFile && !hasValidData ? 'Fix errors and re-upload file to import companies' : undefined}>
            <Button color='primary' disabled={!hasValidData || isSaving} onClick={handleSubmit}>
              Invite company
            </Button>
          </SimpleTooltip>
        </div>
      </div>
    </>
  );
};
